import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { ICirStoreCollection, ICirWeekCollection } from 'common/models/commonModel';
import { AppDispatch } from 'app/store';
import { improveQscTransport } from 'entities/ImproveQsc/ImproveQsc.transport';
import {
  IImproveQscFindingsCollection,
  IImproveQscFindingsCollectionParams,
  IImproveQscStoreCardsCollection,
  IImproveQscStoreCardsCollectionParams,
  IImproveQscSummaryModel,
  IImproveQscSummaryParams,
} from 'entities/ImproveQsc/ImproveQsc.models';

export interface IState {
  // stores
  improveQscStoreCollectionLoading: boolean;
  improveQscStoreCollection: ICirStoreCollection | null;
  improveQscStoreCollectionError: IError | null;
  // weeks
  improveQscWeekCollectionLoading: boolean;
  improveQscWeekCollection: ICirWeekCollection | null;
  improveQscWeekCollectionError: IError | null;
  // summary
  improveQscSummaryModelLoading: boolean;
  improveQscSummaryModel: IImproveQscSummaryModel | null;
  improveQscSummaryModelError: IError | null;
  // findings
  improveQscFindingsCollectionLoading: boolean;
  improveQscFindingsCollection: IImproveQscFindingsCollection | null;
  improveQscFindingsCollectionError: IError | null;
  // store cards
  improveQscStoreCardsCollectionLoading: boolean;
  improveQscStoreCardsCollection: IImproveQscStoreCardsCollection | null;
  improveQscStoreCardsCollectionError: IError | null;
}

const initialState: IState = {
  // stores
  improveQscStoreCollectionLoading: false,
  improveQscStoreCollection: null,
  improveQscStoreCollectionError: null,
  // weeks
  improveQscWeekCollectionLoading: false,
  improveQscWeekCollection: null,
  improveQscWeekCollectionError: null,
  // summary
  improveQscSummaryModelLoading: false,
  improveQscSummaryModel: null,
  improveQscSummaryModelError: null,
  // findings
  improveQscFindingsCollectionLoading: false,
  improveQscFindingsCollection: null,
  improveQscFindingsCollectionError: null,
  // store cards
  improveQscStoreCardsCollectionLoading: false,
  improveQscStoreCardsCollection: null,
  improveQscStoreCardsCollectionError: null,
};

export const improveQscSlice = createSlice({
  name: 'improveQsc',
  initialState,
  reducers: {
    // stores
    setImproveQscStoreCollectionLoading(state, action: PayloadAction<boolean>) {
      state.improveQscStoreCollectionLoading = action.payload;
    },
    setImproveQscStoreCollection(state, action: PayloadAction<ICirStoreCollection | null>) {
      state.improveQscStoreCollection = action.payload;
    },
    setImproveQscStoreCollectionError(state, action: PayloadAction<IError | null>) {
      state.improveQscStoreCollectionError = action.payload;
    },
    // weeks
    setImproveQscWeekCollectionLoading(state, action: PayloadAction<boolean>) {
      state.improveQscWeekCollectionLoading = action.payload;
    },
    setImproveQscWeekCollection(state, action: PayloadAction<ICirWeekCollection | null>) {
      state.improveQscWeekCollection = action.payload;
    },
    setImproveQscWeekCollectionError(state, action: PayloadAction<IError | null>) {
      state.improveQscWeekCollectionError = action.payload;
    },
    // summary
    setImproveQscSummaryModelLoading(state, action: PayloadAction<boolean>) {
      state.improveQscSummaryModelLoading = action.payload;
    },
    setImproveQscSummaryModel(state, action: PayloadAction<IImproveQscSummaryModel | null>) {
      state.improveQscSummaryModel = action.payload;
    },
    setImproveQscSummaryModelError(state, action: PayloadAction<IError | null>) {
      state.improveQscSummaryModelError = action.payload;
    },
    // findings
    setImproveQscFindingsCollectionLoading(state, action: PayloadAction<boolean>) {
      state.improveQscFindingsCollectionLoading = action.payload;
    },
    setImproveQscFindingsCollection(state, action: PayloadAction<IImproveQscFindingsCollection | null>) {
      state.improveQscFindingsCollection = action.payload;
    },
    setImproveQscFindingsCollectionError(state, action: PayloadAction<IError | null>) {
      state.improveQscFindingsCollectionError = action.payload;
    },
    // store cards
    setImproveQscStoreCardsCollectionLoading(state, action: PayloadAction<boolean>) {
      state.improveQscStoreCardsCollectionLoading = action.payload;
    },
    setImproveQscStoreCardsCollection(state, action: PayloadAction<IImproveQscStoreCardsCollection | null>) {
      state.improveQscStoreCardsCollection = action.payload;
    },
    setImproveQscStoreCardsCollectionError(state, action: PayloadAction<IError | null>) {
      state.improveQscStoreCardsCollectionError = action.payload;
    },
  },
});

export const {
  // stores
  setImproveQscStoreCollectionLoading,
  setImproveQscStoreCollection,
  setImproveQscStoreCollectionError,
  // weeks
  setImproveQscWeekCollectionLoading,
  setImproveQscWeekCollection,
  setImproveQscWeekCollectionError,
  // weeks
  setImproveQscSummaryModelLoading,
  setImproveQscSummaryModel,
  setImproveQscSummaryModelError,
  // findings
  setImproveQscFindingsCollectionLoading,
  setImproveQscFindingsCollection,
  setImproveQscFindingsCollectionError,
  // store cards
  setImproveQscStoreCardsCollectionLoading,
  setImproveQscStoreCardsCollection,
  setImproveQscStoreCardsCollectionError,
} = improveQscSlice.actions;
export default improveQscSlice.reducer;

// Thunks

export const getImproveQscStoreCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setImproveQscStoreCollectionLoading(true));

    try {
      const data = await improveQscTransport.getStores();
      dispatch(setImproveQscStoreCollection(data));
    } catch (error) {
      dispatch(setImproveQscStoreCollectionError(error as IError));
    } finally {
      dispatch(setImproveQscStoreCollectionLoading(false));
    }
  }

  return thunk;
};

export const getImproveQscWeekCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setImproveQscWeekCollectionLoading(true));

    try {
      const data = await improveQscTransport.getWeeks();
      dispatch(setImproveQscWeekCollection(data));
    } catch (error) {
      dispatch(setImproveQscWeekCollectionError(error as IError));
    } finally {
      dispatch(setImproveQscWeekCollectionLoading(false));
    }
  }

  return thunk;
};

export const getImproveQscSummaryModel = (params?: IImproveQscSummaryParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setImproveQscSummaryModelLoading(true));

    try {
      const data = await improveQscTransport.getSummary(params);
      dispatch(setImproveQscSummaryModel(data));
    } catch (error) {
      dispatch(setImproveQscSummaryModelError(error as IError));
    } finally {
      dispatch(setImproveQscSummaryModelLoading(false));
    }
  }

  return thunk;
};

export const getImproveQscFindingsCollection = (params?: IImproveQscFindingsCollectionParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setImproveQscFindingsCollectionLoading(true));

    try {
      const data = await improveQscTransport.getFindings(params);
      dispatch(setImproveQscFindingsCollection(data));
    } catch (error) {
      dispatch(setImproveQscFindingsCollectionError(error as IError));
    } finally {
      dispatch(setImproveQscFindingsCollectionLoading(false));
    }
  }

  return thunk;
};

export const getImproveQscStoreCardsCollection = (params?: IImproveQscStoreCardsCollectionParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setImproveQscStoreCardsCollectionLoading(true));

    try {
      const data = await improveQscTransport.getStoreCards(params);
      dispatch(setImproveQscStoreCardsCollection(data));
    } catch (error) {
      dispatch(setImproveQscStoreCardsCollectionError(error as IError));
    } finally {
      dispatch(setImproveQscStoreCardsCollectionLoading(false));
    }
  }

  return thunk;
};
